import React from "react";

/**
 * ProjectVisual — placeholder illustrato per un progetto Ri.Sco.
 * Sfondo caldo greige/rame, illustrazione SVG scelta in base al titolo del progetto.
 *
 * Varianti:
 *   - facade     → progetto 1 · Bonus Facciata (fabbricato 3 piani, 6 unità)
 *   - merge      → progetto 2 · Vomero, fusione di due appartamenti
 *   - openspace  → progetto 3 · Da spazi angusti a open space
 *   - tall       → progetto 4 · Fabbricato 9 piani
 *   - coastal    → progetto 5 · B&B di lusso a Chiaia, vista mare
 */

function pickVariant(project) {
  const t = (project?.title || "").toLowerCase();
  if (/9\s*piani/.test(t)) return "tall";
  if (/facciata|bonus/.test(t)) return "facade";
  if (/vomero|fusione|due\s*(unit|appartamenti)/.test(t)) return "merge";
  if (/open\s*space|angusti|spazi|provincia/.test(t)) return "openspace";
  if (/chiaia|b\s*&?\s*b|vista\s*mare|lusso/.test(t)) return "coastal";
  return "facade";
}

const INK = "#0C0A09";
const COPPER = "#9E3A1E";

/* ---------- illustrazioni ---------- */

function FacadeSVG() {
  // Fabbricato di 3 piani × 2 unità per piano
  return (
    <svg viewBox="0 0 400 500" fill="none" preserveAspectRatio="xMidYMid meet" className="w-[62%] h-auto">
      {/* struttura */}
      <rect x="70" y="90" width="260" height="330" stroke={INK} strokeWidth="2" fill="none" />
      {/* tetto */}
      <path d="M55 90 L200 45 L345 90" stroke={COPPER} strokeWidth="2" fill="none" strokeLinejoin="round" />
      {/* linee orizzontali (solai) */}
      <line x1="70" y1="200" x2="330" y2="200" stroke={INK} strokeWidth="1" strokeDasharray="4 4" />
      <line x1="70" y1="310" x2="330" y2="310" stroke={INK} strokeWidth="1" strokeDasharray="4 4" />
      {/* finestre — 3 piani × 2 finestre */}
      {[110, 220, 330].map((y) => (
        <g key={y}>
          <rect x="105" y={y - 55} width="80" height="80" stroke={INK} strokeWidth="1.5" fill="#F5E4DA" />
          <rect x="215" y={y - 55} width="80" height="80" stroke={INK} strokeWidth="1.5" fill="#F5E4DA" />
          <line x1="145" y1={y - 55} x2="145" y2={y + 25} stroke={INK} strokeWidth="1" />
          <line x1="255" y1={y - 55} x2="255" y2={y + 25} stroke={INK} strokeWidth="1" />
          <line x1="105" y1={y - 15} x2="185" y2={y - 15} stroke={INK} strokeWidth="1" />
          <line x1="215" y1={y - 15} x2="295" y2={y - 15} stroke={INK} strokeWidth="1" />
        </g>
      ))}
      {/* portone */}
      <rect x="170" y="380" width="60" height="40" stroke={COPPER} strokeWidth="2" fill="#F5E4DA" />
      {/* etichetta cappotto termico */}
      <line x1="45" y1="120" x2="65" y2="120" stroke={COPPER} strokeWidth="2" />
      <line x1="45" y1="150" x2="65" y2="150" stroke={COPPER} strokeWidth="2" />
      <line x1="45" y1="180" x2="65" y2="180" stroke={COPPER} strokeWidth="2" />
    </svg>
  );
}

function MergeSVG() {
  // Due appartamenti che si fondono
  return (
    <svg viewBox="0 0 400 500" fill="none" preserveAspectRatio="xMidYMid meet" className="w-[70%] h-auto">
      {/* planimetria unita */}
      <path d="M60 160 L200 160 L200 100 L340 100 L340 400 L200 400 L200 340 L60 340 Z"
        stroke={INK} strokeWidth="2" fill="#F5E4DA" fillOpacity="0.5" />
      {/* divisione originale (linea che sparisce) */}
      <line x1="200" y1="100" x2="200" y2="400" stroke={COPPER} strokeWidth="1.5" strokeDasharray="6 6" />
      {/* etichette appartamenti */}
      <text x="130" y="255" fill={INK} fontSize="20" fontFamily="serif" fontStyle="italic">A</text>
      <text x="265" y="255" fill={INK} fontSize="20" fontFamily="serif" fontStyle="italic">B</text>
      {/* frecce di fusione */}
      <path d="M175 250 L195 250 M225 250 L205 250" stroke={COPPER} strokeWidth="2" strokeLinecap="round" />
      <polygon points="195,244 205,250 195,256" fill={COPPER} />
      <polygon points="205,244 195,250 205,256" fill={COPPER} />
      {/* mobili astratti */}
      <rect x="80" y="180" width="50" height="20" stroke={INK} strokeWidth="1" fill="none" />
      <rect x="80" y="290" width="30" height="30" stroke={INK} strokeWidth="1" fill="none" />
      <rect x="280" y="130" width="40" height="60" stroke={INK} strokeWidth="1" fill="none" />
      <circle cx="290" cy="360" r="18" stroke={INK} strokeWidth="1" fill="none" />
    </svg>
  );
}

function OpenspaceSVG() {
  // Open space con terrazzo + 2 bagni
  return (
    <svg viewBox="0 0 400 500" fill="none" preserveAspectRatio="xMidYMid meet" className="w-[74%] h-auto">
      {/* perimetro */}
      <rect x="55" y="90" width="290" height="240" stroke={INK} strokeWidth="2" fill="#F5E4DA" fillOpacity="0.35" />
      {/* terrazzo esterno */}
      <rect x="55" y="330" width="290" height="80" stroke={COPPER} strokeWidth="2" fill="none" strokeDasharray="6 4" />
      <text x="160" y="378" fill={COPPER} fontSize="13" letterSpacing="0.28em" fontFamily="sans-serif">TERRAZZO</text>
      {/* muro rimosso (linea che sparisce) */}
      <line x1="200" y1="90" x2="200" y2="240" stroke={COPPER} strokeWidth="1.5" strokeDasharray="4 6" />
      {/* isola cucina */}
      <rect x="80" y="140" width="90" height="30" stroke={INK} strokeWidth="1.5" fill="none" />
      <line x1="80" y1="155" x2="170" y2="155" stroke={INK} strokeWidth="1" />
      {/* salone — divano ad angolo */}
      <path d="M75 200 L75 260 L170 260 L170 240 L95 240 L95 200 Z" stroke={INK} strokeWidth="1.5" fill="none" />
      {/* 2 bagni */}
      <rect x="220" y="105" width="55" height="55" stroke={INK} strokeWidth="1.5" fill="none" />
      <text x="235" y="140" fill={COPPER} fontSize="16" fontFamily="serif" fontStyle="italic">1</text>
      <rect x="285" y="105" width="55" height="55" stroke={INK} strokeWidth="1.5" fill="none" />
      <text x="300" y="140" fill={COPPER} fontSize="16" fontFamily="serif" fontStyle="italic">2</text>
      {/* camere da letto */}
      <rect x="220" y="180" width="120" height="65" stroke={INK} strokeWidth="1" fill="none" />
      <rect x="220" y="255" width="120" height="65" stroke={INK} strokeWidth="1" fill="none" />
      {/* letti */}
      <rect x="235" y="195" width="55" height="35" stroke={INK} strokeWidth="1" fill="none" />
      <rect x="235" y="270" width="55" height="35" stroke={INK} strokeWidth="1" fill="none" />
    </svg>
  );
}

function TallSVG() {
  // Fabbricato di 9 piani
  return (
    <svg viewBox="0 0 400 500" fill="none" preserveAspectRatio="xMidYMid meet" className="w-[54%] h-auto">
      {/* silhouette 9 piani */}
      <rect x="110" y="50" width="180" height="410" stroke={INK} strokeWidth="2" fill="#F5E4DA" fillOpacity="0.4" />
      {/* linea copper del tetto */}
      <line x1="105" y1="50" x2="295" y2="50" stroke={COPPER} strokeWidth="3" />
      {/* 9 fasce piani */}
      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((floor) => {
        const y = 50 + floor * (410 / 9);
        return <line key={`sep-${floor}`} x1="110" y1={y} x2="290" y2={y} stroke={INK} strokeWidth="1" strokeDasharray="3 3" />;
      })}
      {/* finestre — 3 per piano */}
      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((floor) => {
        const rowY = 50 + (floor - 1) * (410 / 9) + 12;
        return (
          <g key={`win-${floor}`}>
            <rect x="128" y={rowY} width="30" height="22" stroke={INK} strokeWidth="1" fill="#EFEEE9" />
            <rect x="184" y={rowY} width="30" height="22" stroke={INK} strokeWidth="1" fill="#EFEEE9" />
            <rect x="240" y={rowY} width="30" height="22" stroke={INK} strokeWidth="1" fill="#EFEEE9" />
          </g>
        );
      })}
      {/* numero piani */}
      <text x="60" y="260" fill={COPPER} fontSize="42" fontFamily="serif" fontStyle="italic">9</text>
      <text x="55" y="285" fill={COPPER} fontSize="9" letterSpacing="0.28em" fontFamily="sans-serif">PIANI</text>
    </svg>
  );
}

function CoastalSVG() {
  // Finestra ad arco vista mare + onde
  return (
    <svg viewBox="0 0 400 500" fill="none" preserveAspectRatio="xMidYMid meet" className="w-[70%] h-auto">
      {/* sole/luna sullo sfondo */}
      <circle cx="290" cy="90" r="28" fill={COPPER} fillOpacity="0.35" />
      {/* muro con arco */}
      <path d="M70 420 L70 190 Q70 90 200 90 Q330 90 330 190 L330 420 Z"
        stroke={INK} strokeWidth="2" fill="#F5E4DA" fillOpacity="0.55" />
      {/* linea orizzonte mare */}
      <line x1="90" y1="280" x2="310" y2="280" stroke={COPPER} strokeWidth="1.5" />
      {/* onde */}
      {[300, 320, 340, 360, 380].map((y, i) => (
        <path key={y} d={`M90 ${y} Q140 ${y - 6} 200 ${y} T310 ${y}`}
          stroke={INK} strokeWidth="1" fill="none" strokeOpacity={0.35 + i * 0.1} />
      ))}
      {/* divisori finestra */}
      <line x1="200" y1="90" x2="200" y2="420" stroke={INK} strokeWidth="1" />
      <line x1="70" y1="280" x2="330" y2="280" stroke={INK} strokeWidth="1" />
      {/* doccia a vista (simbolica): dettaglio pattern */}
      <g transform="translate(115 400)">
        <rect x="0" y="0" width="60" height="6" fill={COPPER} />
        {[0, 12, 24, 36, 48].map((x) => (
          <line key={x} x1={x + 8} y1="6" x2={x + 8} y2="20" stroke={COPPER} strokeWidth="1" />
        ))}
      </g>
      {/* etichetta "MARE" */}
      <text x="240" y="260" fill={COPPER} fontSize="10" letterSpacing="0.32em" fontFamily="sans-serif">VISTA MARE</text>
    </svg>
  );
}

const VARIANT_MAP = {
  facade: FacadeSVG,
  merge: MergeSVG,
  openspace: OpenspaceSVG,
  tall: TallSVG,
  coastal: CoastalSVG,
};

const VARIANT_LABEL = {
  facade: "Cappotto termico · 3 piani · 6 unità",
  merge: "Fusione di due appartamenti",
  openspace: "Da spazi chiusi a open space",
  tall: "Riqualificazione · 9 piani",
  coastal: "Rifiniture di pregio · Vista mare",
};

export default function ProjectVisual({ project }) {
  const variant = pickVariant(project);
  const Illustration = VARIANT_MAP[variant] || FacadeSVG;
  const label = VARIANT_LABEL[variant];
  const yr = project?.year || "";
  const inProgress = !project?.year;
  return (
    <div className="relative w-full h-full overflow-hidden">
      {/* Base: gradiente greige→sabbia caldo */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(160deg, #F5E4DA 0%, #EFEEE9 45%, #E4DBC7 100%)",
        }}
      />
      {/* Texture grana sottile */}
      <div
        className="absolute inset-0 opacity-[0.08] mix-blend-multiply"
        style={{
          backgroundImage:
            "radial-gradient(#0C0A09 1px, transparent 1px)",
          backgroundSize: "3px 3px",
        }}
      />
      {/* Cornice sottile rame */}
      <div className="absolute inset-4 border border-risco-accent/25 rounded-sm pointer-events-none" />

      {/* Contenuto */}
      <div className="relative w-full h-full flex flex-col justify-between p-6 md:p-7">
        <div className="flex items-start justify-between gap-3">
          <span className="text-[10px] uppercase tracking-[0.28em] text-risco-accent font-semibold">
            Ri.Sco. · Progetto
          </span>
          {inProgress ? (
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-risco-accent text-white text-[9px] uppercase tracking-[0.22em] font-semibold whitespace-nowrap">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
              In corso
            </span>
          ) : (
            <span className="text-[10px] uppercase tracking-[0.28em] text-risco-ink/60 font-mono">
              {yr}
            </span>
          )}
        </div>

        <div className="flex-1 flex items-center justify-center py-4">
          <Illustration />
        </div>

        <div>
          <div className="h-px w-10 bg-risco-accent mb-3" />
          <div className="font-display italic text-lg md:text-xl text-risco-ink leading-tight">
            {label}
          </div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-risco-ink/55 mt-1">
            {inProgress ? "In fase di ultimazione" : "Foto in arrivo"}
          </div>
        </div>
      </div>
    </div>
  );
}
